import { eEnvelopeStatusType, eGrafanaEnvironments, eIdentityVerificationSource, ePartySubType, ePartyType, eSystemEventType } from "./enums";
import { Party } from "./interfaces/parties/party";
import { SystemEvent } from "./interfaces/system_events/system-event";

import { AbstractControl } from "@angular/forms";
import { Address } from "./interfaces/address/address";
import { Province } from "./interfaces/province";
import { HttpHeaders } from "@angular/common/http";
import { IdentityVerificationSource } from "./interfaces/identity-verification-source/identity-verification-source";

export class Utilities {

  static deepCopy(obj: any): any {
    if (obj === null || typeof obj !== "object") {
      return obj;
    }
    if (obj instanceof Date) {
      return new Date(obj.getTime());
    }
    if (Array.isArray(obj)) {
      return obj.map(item => Utilities.deepCopy(item));
    }
    if (typeof obj === "object") {
      const copiedObject = Object.assign({}, obj);
      Object.keys(copiedObject).forEach(key => {
        copiedObject[key] = Utilities.deepCopy(obj[key]);
      });
      return copiedObject;
    }
    return obj;
  }

  static italicize(input: string | null | undefined): string {
    if (!input) {
      return '';
    }

    return `<span class="italics">${input}</span>`;
  }

  static bold(input: string | null | undefined): string {
    if (!input) {
      return '';
    }

    return `<span class="bold">${input}</span>`;
  }

  static validDateValidator() {
    return (control: AbstractControl): { [key: string]: any; } | null => {
      const value = control.value;
      if (value) {
        const date = new Date(value);
        if (isNaN(date.getTime())) {
          // Invalid date
          return { invalidDate: 'Invalid date' };
        } else {
          // Check if the year is greater than 9999
          const year = date.getFullYear();
          if (year > 9999) {
            return { invalidDate: 'Invalid date' };
          }
        }
      }
      return null;
    };
  }

  static spacePascalCase(input: string): string {
    return input?.replace(/(?<=[a-z])([A-Z])/g, ' $1') ?? input;
  }

  static removeNonAlphaNumericChars(input: string | null | undefined): string | null | undefined {
    return input?.replace(/[^a-zA-Z0-9]/g, '') ?? input;
  }

  static sortByJurisdiction<T>(getKey: (element: T) => string): (a: T, b: T) => number {
    const orderedJurisdictions = ['BC', 'AB', 'SK', 'MB', 'ON', 'QC', 'NL', 'NB', 'NS', 'PE', 'YT', 'NT', 'NU'];

    return (a, b) => {
      const indexA = orderedJurisdictions.indexOf(getKey(a));
      const indexB = orderedJurisdictions.indexOf(getKey(b));

      const safeIndexA = indexA === -1 ? Number.MAX_VALUE : indexA;
      const safeIndexB = indexB === -1 ? Number.MAX_VALUE : indexB;

      return safeIndexA - safeIndexB;
    };
  }

  public static isDebtor(party: Party) {
    return party.partyTypeID === ePartyType.businessDebtor || party.partyTypeID === ePartyType.individualDebtor;
  }

  public static isSecuredParty(party: Party) {
    return party.partyTypeID === ePartyType.businessSecuredParty || party.partyTypeID === ePartyType.individualSecuredParty;
  }

  public static isDealer(party: Party) {
    return party.partyTypeID === ePartyType.businessDealer || party.partyTypeID === ePartyType.individualDealer;
  }

  public static isRegisteringAgent(party: Party) {
    return party.partyTypeID === ePartyType.businessRegisteringAgent || party.partyTypeID === ePartyType.individualRegisteringAgent;
  }

  public static isAssignor(party: Party) {
    return Utilities.isSecuredParty(party) && party.partySubType === ePartySubType.assignor;
  }

  public static isAssignee(party: Party) {
    return Utilities.isSecuredParty(party) && party.partySubType === ePartySubType.assignee;
  }

  public static isBusiness(party: Party) {
    return party.partyTypeID === ePartyType.businessDebtor ||
      party.partyTypeID === ePartyType.businessSecuredParty ||
      party.partyTypeID === ePartyType.businessDealer ||
      party.partyTypeID === ePartyType.businessRegisteringAgent ||
      party.partyTypeID === ePartyType.businessBeneficialOwner ||
      party.partyTypeID === ePartyType.businessShareholder;
  }

  public static isIndividual(party: Party) {
    return party.partyTypeID === ePartyType.individualDebtor ||
      party.partyTypeID === ePartyType.individualSecuredParty ||
      party.partyTypeID === ePartyType.individualDealer ||
      party.partyTypeID === ePartyType.individualRegisteringAgent ||
      party.partyTypeID === ePartyType.individualBeneficialOwner ||
      party.partyTypeID === ePartyType.individualShareholder ||
      party.partyTypeID === ePartyType.individualSigningAuthority;
  }

  public static isAttestor(party: Party) {
    return party.partyTypeID === ePartyType.individualAttestor;
  }

  public static isSigningAuthority(party: Party) {
    return party.partyTypeID === ePartyType.individualSigningAuthority;
  }

  public static getPartyTypeIcon(party: Party): string {
    if (Utilities.isIndividual(party)) {
      return 'person';
    } else {
      return 'business_center';
    }
  }

  /** Set by EnumLabelService so these static helpers can return translated enum labels. */
  public static enumLabel?: (enumName: string, value: string, fallbackName: string) => string;

  public static getPartyTypeString(party: Party): string {
    const english = Utilities.getPartyTypeStringEnglish(party);
    const id = party.partyTypeID ?? party.partyType?.id;
    return id != null && Utilities.enumLabel ? Utilities.enumLabel('partyType', String(id), english) : english;
  }

  /** The party's role without Business/Individual, e.g. 'Secured Party' (translated when available). */
  public static getPartyRoleLabel(party: Party): string {
    const role = Utilities.getPartyTypeStringEnglish(party).split(' ').slice(1).join(' ');
    return role && Utilities.enumLabel ? Utilities.enumLabel('normalizedPartyType', role.replace(/\s/g, ''), role) : role;
  }

  private static getPartyTypeStringEnglish(party: Party): string {
    switch (party.partyTypeID ?? party.partyType.id) {
      case ePartyType.businessDebtor:
        return "Business Debtor";
      case ePartyType.individualDebtor:
        return "Individual Debtor";
      case ePartyType.businessSecuredParty:
        return "Business Secured Party";
      case ePartyType.individualSecuredParty:
        return "Individual Secured Party";
      case ePartyType.businessRegisteringAgent:
        return "Business Registering Agent";
      case ePartyType.individualRegisteringAgent:
        return "Individual Registering Agent";
      case ePartyType.individualDealer:
        return "Individual Dealer";
      case ePartyType.businessDealer:
        return "Business Dealer";
      default:
        return "Party";
    }
  }

  public static getIdRequirementsString(idvSources: eIdentityVerificationSource): string {
    const english = Utilities.getIdRequirementsStringEnglish(idvSources);
    return english && Utilities.enumLabel ? Utilities.enumLabel('identityVerificationSource', idvSources, english) : english;
  }

  private static getIdRequirementsStringEnglish(idvSources: eIdentityVerificationSource): string {
    switch (idvSources) {
      case eIdentityVerificationSource.EfxAmlSingleSource:
        return "Equifax AML";
      case eIdentityVerificationSource.TuCreditBureau:
        return "Transunion Credit Bureau";
      case eIdentityVerificationSource.EfxDvs:
        return "Equifax DVS";
      case eIdentityVerificationSource.EntrustDvs:
        return "Entrust DVS";
      default:
        return "";
    }
  }

  public static getEnvelopeIcon(status: eEnvelopeStatusType): string {
    switch (status) {
      case eEnvelopeStatusType.open:
        return 'folder_open';
      case eEnvelopeStatusType.sealed:
        return 'folder';
      case eEnvelopeStatusType.error:
        return 'error_outline';
      case eEnvelopeStatusType.imported:
        return 'history';
      case eEnvelopeStatusType.resultsPending:
        return 'archive';
      case eEnvelopeStatusType.complete:
        return 'check_circle_outline';
      case eEnvelopeStatusType.documentProcessing:
        return 'article_shortcut';
      default:
        return '';
    }
  }

  public static getName(party: Party | undefined, isAdmin: boolean): string {
    if (!party) {
      return '';
    } else if (this.isIndividual(party)) {
      return this.getIndividualName(party, isAdmin);
    } else if (this.isBusiness(party)) {
      return party.busName ?? '';
    } else {
      return '';
    }
  }

  public static getIndividualName(party: Party | undefined, isAdmin: boolean): string {
    let fullName = '';

    if (!party) {
      return fullName;
    }

    if (party.firstName) {
      fullName = party.firstName;
    }

    if (party.middleName) {
      fullName = `${fullName} ${isAdmin ? Utilities.italicize(party.middleName) : party.middleName}`;
    }

    if (party.lastName) {
      fullName = `${fullName} ${isAdmin ? Utilities.bold(party.lastName) : party.lastName}`;
    }

    return fullName.trim();
  }

  public static formatAddress(address: Address | undefined, isAdmin: boolean = false): string {
    if (!address) {
      return '';
    }

    const streetLine = [
      (isAdmin ? Utilities.italicize(address?.unit) : address?.unit),
      address?.streetNumber,
      (isAdmin ? Utilities.bold(address?.streetName) : address?.streetName),
      address?.quadrant
    ].filter(field => field).join(' ').trim();

    const addressParts = [
      streetLine,
      address?.city,
      address?.jurisdiction,
      address?.postalCode,
      address?.countryCode
    ].filter(field => field);

    return addressParts.join(', ').trim();
  }

  public static provinces: Province[] = [
    { value: 'AB', viewValue: 'AB' },
    { value: 'BC', viewValue: 'BC' },
    { value: 'MB', viewValue: 'MB' },
    { value: 'NB', viewValue: 'NB' },
    { value: 'NL', viewValue: 'NL' },
    { value: 'NS', viewValue: 'NS' },
    { value: 'ON', viewValue: 'ON' },
    { value: 'PE', viewValue: 'PE' },
    { value: 'QC', viewValue: 'QC' },
    { value: 'SK', viewValue: 'SK' },
    { value: 'NT', viewValue: 'NT' },
    { value: 'NU', viewValue: 'NU' },
    { value: 'YT', viewValue: 'YT' },
    { value: '_F', viewValue: 'FED' }
  ];

  public static usStates: Province[] = [
    { value: 'AL', viewValue: 'AL' },
    { value: 'AK', viewValue: 'AK' },
    { value: 'AZ', viewValue: 'AZ' },
    { value: 'AR', viewValue: 'AR' },
    { value: 'CA', viewValue: 'CA' },
    { value: 'CO', viewValue: 'CO' },
    { value: 'CT', viewValue: 'CT' },
    { value: 'DE', viewValue: 'DE' },
    { value: 'FL', viewValue: 'FL' },
    { value: 'GA', viewValue: 'GA' },
    { value: 'HI', viewValue: 'HI' },
    { value: 'ID', viewValue: 'ID' },
    { value: 'IL', viewValue: 'IL' },
    { value: 'IN', viewValue: 'IN' },
    { value: 'IA', viewValue: 'IA' },
    { value: 'KS', viewValue: 'KS' },
    { value: 'KY', viewValue: 'KY' },
    { value: 'LA', viewValue: 'LA' },
    { value: 'ME', viewValue: 'ME' },
    { value: 'MD', viewValue: 'MD' },
    { value: 'MA', viewValue: 'MA' },
    { value: 'MI', viewValue: 'MI' },
    { value: 'MN', viewValue: 'MN' },
    { value: 'MS', viewValue: 'MS' },
    { value: 'MO', viewValue: 'MO' },
    { value: 'MT', viewValue: 'MT' },
    { value: 'NE', viewValue: 'NE' },
    { value: 'NV', viewValue: 'NV' },
    { value: 'NH', viewValue: 'NH' },
    { value: 'NJ', viewValue: 'NJ' },
    { value: 'NM', viewValue: 'NM' },
    { value: 'NY', viewValue: 'NY' },
    { value: 'NC', viewValue: 'NC' },
    { value: 'ND', viewValue: 'ND' },
    { value: 'OH', viewValue: 'OH' },
    { value: 'OK', viewValue: 'OK' },
    { value: 'OR', viewValue: 'OR' },
    { value: 'PA', viewValue: 'PA' },
    { value: 'RI', viewValue: 'RI' },
    { value: 'SC', viewValue: 'SC' },
    { value: 'SD', viewValue: 'SD' },
    { value: 'TN', viewValue: 'TN' },
    { value: 'TX', viewValue: 'TX' },
    { value: 'UT', viewValue: 'UT' },
    { value: 'VT', viewValue: 'VT' },
    { value: 'VA', viewValue: 'VA' },
    { value: 'WA', viewValue: 'WA' },
    { value: 'WV', viewValue: 'WV' },
    { value: 'WI', viewValue: 'WI' },
    { value: 'WY', viewValue: 'WY' },

    // Territories
    { value: 'DC', viewValue: 'DC' }, // District of Columbia
    { value: 'AS', viewValue: 'AS' }, // American Samoa
    { value: 'GU', viewValue: 'GU' }, // Guam
    { value: 'MP', viewValue: 'MP' }, // Northern Mariana Islands
    { value: 'PR', viewValue: 'PR' }, // Puerto Rico
    { value: 'VI', viewValue: 'VI' }, // U.S. Virgin Islands

    // Armed Forces “states” (often included)
    { value: 'AA', viewValue: 'AA' }, // Armed Forces Americas
    { value: 'AE', viewValue: 'AE' }, // Armed Forces Europe
    { value: 'AP', viewValue: 'AP' }  // Armed Forces Pacific
  ];

  public static sanitizeRelativeURL(rawRelativeURL: string | null | undefined, portalPrefix: string, defaultValue: string = "/dashboard"): string {
    if (rawRelativeURL) {
      try {
        const parsedUrl = new URL(rawRelativeURL, window.location.origin);

        const allowedOrigins = [
          'https://localhost:4200',
          `https://${portalPrefix}.reghub.dev`,
          `https://${portalPrefix}.uat.reghub.ca`,
          `https://${portalPrefix}.reghub.ca`
        ];

        if (allowedOrigins.includes(parsedUrl.origin)) {
          return parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
        }
      } catch { }
    }

    return '/dashboard';
  }

  public static sanitizeURL(rawURL: string | null | undefined, portalPrefix: string): URL {
    if (rawURL) {
      try {
        const parsedUrl = new URL(rawURL);

        const allowedOrigins = [
          // 'https://localhost:4200',
          `https://${portalPrefix}.reghub.dev`,
          `https://${portalPrefix}.uat.reghub.ca`,
          `https://${portalPrefix}.reghub.ca`
        ];

        if (allowedOrigins.includes(parsedUrl.origin)) {
          return parsedUrl;
        }
      } catch { }
    }
    return new URL("about:blank");
  }

  public static generateHeaders = () => {
    return {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };
  };

  public static isSystemEventOfType(event: Pick<SystemEvent, 'systemEventTypeID'>, type: eSystemEventType): boolean {
    const eventType = event.systemEventTypeID as unknown;

    return eventType === type || eventType === eSystemEventType[type];
  }

  // The API sends UTC dates without a 'Z', and new Date() would read those as local time.
  public static parseUtcDate(value: Date | string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return value;
    }

    return new Date(/(Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`);
  }

  // The banner shows from bannerStartDate (or startDate) until endDate.
  public static isSystemEventDisplayed(event: SystemEvent, now: Date = new Date()): boolean {
    const displayStart = Utilities.parseUtcDate(event.bannerStartDate ?? event.startDate);
    const endDate = Utilities.parseUtcDate(event.endDate);

    return (!displayStart || displayStart <= now) && (!endDate || now <= endDate);
  }

  // Outages and maintenance stay informational until the service actually goes down.
  public static isSystemEventNotice(event: SystemEvent, now: Date = new Date()): boolean {
    if (Utilities.isSystemEventOfType(event, eSystemEventType.GeneralNotice)) {
      return true;
    }

    const startDate = Utilities.parseUtcDate(event.startDate);

    return startDate !== null && now < startDate;
  }

  public static getEnvironment(): eGrafanaEnvironments {
    const hostname = window.location.hostname;
    if (hostname.includes('dev')) {
      return eGrafanaEnvironments.dev;
    } else if (hostname.includes('localhost')) {
      return eGrafanaEnvironments.dev;
    } else if (hostname.includes('uat')) {
      return eGrafanaEnvironments.uat;
    } else {
      return eGrafanaEnvironments.prod;
    }
  }

  public static generateAgentPassword(): string {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    let secret = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');

    const specialChars = '!@#$%^&*';
    const specialChar = specialChars[bytes[0] % specialChars.length];
    const digit = (bytes[1] % 10).toString();
    const upperChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const upperChar = upperChars[bytes[2] % upperChars.length];

    const pos1 = bytes[3] % secret.length;
    secret = secret.slice(0, pos1) + specialChar + secret.slice(pos1);
    const pos2 = bytes[4] % secret.length;
    secret = secret.slice(0, pos2) + digit + secret.slice(pos2);
    const pos3 = bytes[5] % secret.length;
    secret = secret.slice(0, pos3) + upperChar + secret.slice(pos3);

    return secret;
  }
}
